import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BookOpen, RefreshCw, CheckSquare, Square } from 'lucide-react';
import { colors, API, fmt, authHeaders, podeGerenciarCartoes } from '../utils/theme';
import { Modal, LoadingSpinner, EmptyState } from '../components/UI';

// ============================================================================
//  Plano de Contas (03/09/2026) — espelho da tela do TechMiles, no Finance.
//  O plano é UM só e mora no TechMiles; aqui cada lançamento do mês recebe o
//  id da SUBCATEGORIA (grupo não recebe lançamento). Valor por subcategoria,
//  drill nos lançamentos e reclassificação na hora — inclusive em massa.
//  Rótulo automático do extrator do PDF (COMPRAS, TRANSPORTE…) NÃO conta como
//  categoria do plano: aparece no chip "sem categoria" até alguém classificar.
// ============================================================================

const mesAtual = () => new Date().toISOString().slice(0, 7);
const fmtData = (d) => d && /^\d{4}-\d{2}-\d{2}/.test(d) ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(2, 4)}` : (d || '—');

const thS = { padding: '8px 10px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' };
const tdS = { padding: '7px 10px', color: colors.text, fontSize: 13, borderBottom: `1px solid ${colors.border}22` };
const inp = {
  padding: '7px 10px', borderRadius: 8, border: `1px solid ${colors.border}`,
  background: colors.bg, color: colors.text, fontSize: 13, boxSizing: 'border-box',
};

function Chip({ label, valor, cor, onClick, ativo }) {
  return (
    <button onClick={onClick} title={onClick ? 'Clique para ver os lançamentos' : undefined}
      style={{
        cursor: onClick ? 'pointer' : 'default', borderRadius: 10, padding: '8px 14px', textAlign: 'left',
        background: ativo ? `${cor}18` : colors.card, border: `1px solid ${ativo ? cor : colors.border}`,
        minWidth: 170,
      }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color: cor, marginTop: 2 }}>{valor}</div>
    </button>
  );
}

// Select de subcategoria: opções agrupadas por grupo (optgroup = grupo, que
// não é selecionável — regra do plano). Valor atual fora da lista (inativa)
// entra como opção só pra continuar exibida.
function SelectSubcategoria({ value, opcoes, onChange, style, placeholder = '— sem categoria' }) {
  const grupos = useMemo(() => {
    const m = new Map();
    for (const o of opcoes) (m.get(o.grupo) || m.set(o.grupo, []).get(o.grupo)).push(o);
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [opcoes]);
  const naLista = value != null && opcoes.some(o => o.xm_categoria_id === value);
  return (
    <select value={value == null ? '' : String(value)} onChange={e => onChange(e.target.value ? parseInt(e.target.value) : null)}
      style={{ ...inp, ...style, color: value != null ? colors.info : colors.textDim,
        borderColor: value != null ? `${colors.info}66` : colors.border }}>
      <option value="">{placeholder}</option>
      {value != null && !naLista && <option value={String(value)}>#{value} (inativa)</option>}
      {grupos.map(([g, subs]) => (
        <optgroup key={g} label={g}>
          {subs.map(s => <option key={s.xm_categoria_id} value={String(s.xm_categoria_id)}>{s.nome}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

// Modal com os lançamentos de uma linha (subcategoria / sem categoria / não
// conciliados) — reclassifica um a um ou em massa.
function LancamentosModal({ mes, filtro, opcoes, podeEditar, onClose, onChanged }) {
  const [rows, setRows] = useState(null);
  const [erro, setErro] = useState('');
  const [conc, setConc] = useState(''); // '' | '1' | '0'
  const [busca, setBusca] = useState('');
  const [sel, setSel] = useState(new Set());
  const [subLote, setSubLote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mudou, setMudou] = useState(false);

  const carregar = useCallback(() => {
    const p = new URLSearchParams({ mes });
    if (filtro.xm_categoria_id != null) p.set('xm_categoria_id', filtro.xm_categoria_id);
    if (filtro.sem_categoria) p.set('sem_categoria', '1');
    if (filtro.nao_conciliado) p.set('nao_conciliado', '1');
    if (conc) p.set('conciliado', conc);
    if (busca.trim()) p.set('busca', busca.trim());
    setErro('');
    fetch(`${API}/plano-contas/lancamentos?${p}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { if (d.error) throw new Error(d.error); setRows(d.items || []); })
      .catch(e => { setErro(e.message); setRows([]); });
  }, [mes, filtro, conc, busca]);
  useEffect(() => { const t = setTimeout(carregar, busca ? 300 : 0); return () => clearTimeout(t); }, [carregar, busca]);

  const patch = async (id, xmId) => {
    try {
      const r = await fetch(`${API}/plano-contas/lancamentos/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ xm_categoria_id: xmId }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setMudou(true);
      // Atualiza a linha na hora; a lista só é refeita quando a modal fecha.
      setRows(rs => rs.map(x => x.id === id ? {
        ...x, xm_categoria_id: xmId,
        grupo: d.categoria ? d.categoria.grupo : x.grupo,
        categoria_rotulo: d.categoria ? d.categoria.nome : x.categoria_rotulo,
      } : x));
    } catch (e) { alert('Erro ao classificar: ' + e.message); }
  };

  const aplicarLote = async () => {
    const ids = [...sel];
    if (!ids.length || subLote == null) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/plano-contas/lancamentos/classificar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ids, xm_categoria_id: subLote }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setMudou(true); setSel(new Set()); setSubLote(null);
      carregar();
    } catch (e) { alert('Erro ao classificar em massa: ' + e.message); }
    setBusy(false);
  };

  const fechar = () => { if (mudou) onChanged(); onClose(); };
  const todosSel = rows && rows.length > 0 && rows.every(r => sel.has(r.id));
  const toggleTodos = () => setSel(todosSel ? new Set() : new Set(rows.map(r => r.id)));
  const total = (rows || []).reduce((s, r) => s + (r.valor || 0), 0);

  return (
    <Modal isOpen onClose={fechar} title={filtro.titulo} width={1100}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar descrição, cartão ou rótulo…"
          style={{ ...inp, width: 260 }} />
        <select value={conc} onChange={e => setConc(e.target.value)} style={inp}>
          <option value="">Conciliado: todos</option>
          <option value="1">Só conciliados</option>
          <option value="0">Só NÃO conciliados</option>
        </select>
        <span style={{ fontSize: 12, color: colors.textMuted }}>
          {rows ? `${rows.length} lançamento(s) · ${fmt(total)}` : 'Carregando…'}
        </span>
        <div style={{ flex: 1 }} />
        {podeEditar && sel.size > 0 && (
          <>
            <SelectSubcategoria value={subLote} opcoes={opcoes} onChange={setSubLote}
              placeholder="Subcategoria para aplicar…" style={{ minWidth: 260 }} />
            <button onClick={aplicarLote} disabled={busy || subLote == null}
              style={{
                padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                background: subLote == null ? colors.border : colors.primary, color: '#fff',
              }}>
              {busy ? 'Aplicando…' : `Aplicar a ${sel.size}`}
            </button>
          </>
        )}
      </div>
      {erro && <div style={{ color: colors.danger, fontSize: 13, marginBottom: 10 }}>{erro}</div>}
      {!rows ? <LoadingSpinner /> : rows.length === 0 ? <EmptyState message="Nenhum lançamento neste filtro" /> : (
        <div style={{ overflowX: 'auto', maxHeight: '62vh', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                {podeEditar && (
                  <th style={{ ...thS, width: 28 }}>
                    <button onClick={toggleTodos} title="Selecionar todos" style={{ background: 'none', border: 'none', cursor: 'pointer', color: todosSel ? colors.primary : colors.textDim, padding: 0 }}>
                      {todosSel ? <CheckSquare size={15} /> : <Square size={15} />}
                    </button>
                  </th>
                )}
                <th style={thS}>Data</th>
                <th style={thS}>Cartão / conta</th>
                <th style={thS}>Descrição</th>
                <th style={{ ...thS, textAlign: 'right' }}>Valor</th>
                <th style={thS}>Conciliado</th>
                <th style={thS}>Rótulo atual</th>
                <th style={thS}>Subcategoria do plano</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ background: sel.has(r.id) ? `${colors.primary}10` : 'transparent' }}>
                  {podeEditar && (
                    <td style={tdS}>
                      <button onClick={() => setSel(s => { const n = new Set(s); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: sel.has(r.id) ? colors.primary : colors.textDim, padding: 0 }}>
                        {sel.has(r.id) ? <CheckSquare size={15} /> : <Square size={15} />}
                      </button>
                    </td>
                  )}
                  <td style={{ ...tdS, whiteSpace: 'nowrap', color: colors.textMuted }}>{fmtData(r.data)}</td>
                  <td style={{ ...tdS, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.cartao || ''}>{r.cartao || '—'}</td>
                  <td style={{ ...tdS, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.descricao || ''}>
                    {r.descricao}{r.parcela ? <span style={{ color: colors.textDim, marginLeft: 6, fontSize: 11 }}>[{r.parcela}]</span> : null}
                    {r.pessoal && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#f59e0b' }}>PESSOAL</span>}
                  </td>
                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmt(r.valor)}</td>
                  <td style={tdS}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: r.conciliado ? colors.success : colors.warning }}>
                      {r.conciliado ? 'sim' : 'não'}
                    </span>
                  </td>
                  <td style={{ ...tdS, fontSize: 12, color: r.xm_categoria_id != null ? colors.textMuted : colors.textDim, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={`${r.grupo} / ${r.categoria_rotulo}`}>
                    {r.xm_categoria_id != null ? `${r.grupo} / ${r.categoria_rotulo}` : (r.categoria_rotulo || '—')}
                  </td>
                  <td style={tdS}>
                    {podeEditar
                      ? <SelectSubcategoria value={r.xm_categoria_id} opcoes={opcoes} onChange={v => patch(r.id, v)} style={{ minWidth: 240, padding: '4px 8px', fontSize: 12 }} />
                      : <span style={{ fontSize: 12, color: r.xm_categoria_id != null ? colors.info : colors.textDim }}>
                          {r.xm_categoria_id != null ? r.categoria_rotulo : '— sem categoria'}
                        </span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

export default function PlanoContasPage() {
  const podeEditar = podeGerenciarCartoes();
  const [mes, setMes] = useState(mesAtual());
  const [busca, setBusca] = useState('');
  const [grupoSel, setGrupoSel] = useState('');
  const [soAtivas, setSoAtivas] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [modal, setModal] = useState(null); // { titulo, xm_categoria_id?, sem_categoria?, nao_conciliado? }

  const carregar = useCallback(() => {
    if (!/^\d{4}-\d{2}$/.test(mes)) return;
    setLoading(true); setErro('');
    fetch(`${API}/plano-contas?mes=${mes}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { if (d.error) throw new Error(d.error); setData(d); })
      .catch(e => { setErro(e.message); setData(null); })
      .finally(() => setLoading(false));
  }, [mes]);
  useEffect(() => { carregar(); }, [carregar]);

  const grupos = useMemo(() => {
    if (!data) return [];
    const b = busca.trim().toLowerCase();
    return (data.grupos || [])
      .filter(g => !grupoSel || g.grupo === grupoSel)
      .map(g => ({
        ...g,
        subcategorias: g.subcategorias.filter(s =>
          (!soAtivas || s.ativo || s.qtd > 0) &&
          (!b || s.nome.toLowerCase().includes(b) || g.grupo.toLowerCase().includes(b))),
      }))
      .filter(g => g.subcategorias.length);
  }, [data, busca, grupoSel, soAtivas]);

  const nomesGrupos = useMemo(() => (data?.grupos || []).map(g => g.grupo), [data]);
  const opcoes = data?.opcoes || [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <BookOpen size={22} /> Plano de Contas
          </h1>
          <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
            Grupo (categoria) não recebe lançamento — sempre lança-se na <strong>subcategoria</strong>.
            O plano é um só e mora no TechMiles; aqui cada lançamento do mês recebe a subcategoria dele.
          </div>
        </div>
        <button onClick={carregar} style={{ ...inp, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={13} /> Recarregar
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', margin: '14px 0' }}>
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar grupo ou subcategoria…" style={{ ...inp, width: 260 }} />
        <select value={grupoSel} onChange={e => setGrupoSel(e.target.value)} style={inp}>
          <option value="">Grupo: todos</option>
          {nomesGrupos.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: colors.text, cursor: 'pointer' }}>
          <input type="checkbox" checked={soAtivas} onChange={e => setSoAtivas(e.target.checked)} /> Só ativas
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: colors.textMuted }}>
          Mês <input type="month" value={mes} onChange={e => setMes(e.target.value)} style={inp} />
        </label>
        {data && (
          <span style={{ fontSize: 12, color: colors.textMuted }}>
            {data.qtd} lançamento(s) · <strong style={{ color: colors.text }}>{fmt(data.total)}</strong>
          </span>
        )}
      </div>

      {data && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <Chip label="Sem categoria" cor={colors.warning}
            valor={`${data.sem_categoria.qtd} · ${fmt(data.sem_categoria.total)}`}
            onClick={() => setModal({ titulo: `Sem categoria do plano — ${mes}`, sem_categoria: true })} />
          <Chip label="Não conciliados" cor={colors.info}
            valor={`${data.nao_conciliados.qtd} · ${fmt(data.nao_conciliados.total)}`}
            onClick={() => setModal({ titulo: `Não conciliados — ${mes}`, nao_conciliado: true })} />
          {data.pessoal_sem_categoria?.qtd > 0 && (
            <Chip label="Pessoal (fora do resultado)" cor={'#f59e0b'}
              valor={`${data.pessoal_sem_categoria.qtd} · ${fmt(data.pessoal_sem_categoria.total)}`} />
          )}
        </div>
      )}

      {erro && <div style={{ color: colors.danger, fontSize: 13, marginBottom: 10 }}>{erro}</div>}
      {loading && !data ? <LoadingSpinner /> : !data ? null : grupos.length === 0 ? (
        <EmptyState message={nomesGrupos.length ? 'Nenhuma subcategoria neste filtro' : 'O espelho do plano de contas está vazio — o TechMiles ainda não sincronizou as subcategorias'} />
      ) : (
        <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.border}`, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th style={thS}>Grupo</th>
                <th style={thS}>Subcategoria</th>
                <th style={{ ...thS, textAlign: 'right' }}>Valor no mês (Finance)</th>
                <th style={{ ...thS, textAlign: 'right' }}>Qtd</th>
                <th style={{ ...thS, textAlign: 'right' }}>Não conciliados</th>
                <th style={thS}>Status</th>
              </tr>
            </thead>
            <tbody>
              {grupos.map(g => (
                <React.Fragment key={g.grupo}>
                  <tr style={{ background: `${colors.primary}0c`, borderTop: `1px solid ${colors.border}` }}>
                    <td style={{ ...tdS, fontWeight: 800, whiteSpace: 'nowrap' }} colSpan={2}>{g.grupo}</td>
                    <td style={{ ...tdS, textAlign: 'right', fontWeight: 800 }}>{g.total ? fmt(g.total) : <span style={{ color: colors.textDim }}>—</span>}</td>
                    <td style={{ ...tdS, textAlign: 'right', color: colors.textMuted }}>{g.qtd || '—'}</td>
                    <td style={{ ...tdS, textAlign: 'right', color: g.qtd_nao_conciliados ? colors.warning : colors.textDim }}>
                      {g.qtd_nao_conciliados ? `${g.qtd_nao_conciliados} · ${fmt(g.total_nao_conciliados)}` : '—'}
                    </td>
                    <td style={tdS} />
                  </tr>
                  {g.subcategorias.map(s => (
                    <tr key={s.xm_categoria_id}>
                      <td style={tdS} />
                      <td style={{ ...tdS, color: s.ativo ? colors.text : colors.textDim }}>{s.nome}</td>
                      <td style={{ ...tdS, textAlign: 'right' }}>
                        {s.qtd > 0 ? (
                          <button onClick={() => setModal({ titulo: `${g.grupo} / ${s.nome} — ${mes}`, xm_categoria_id: s.xm_categoria_id })}
                            title="Ver os lançamentos do mês nesta subcategoria"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.info, fontWeight: 700, fontSize: 13, textDecoration: 'underline dotted', padding: 0 }}>
                            {fmt(s.total)}
                          </button>
                        ) : <span style={{ color: colors.textDim }}>—</span>}
                      </td>
                      <td style={{ ...tdS, textAlign: 'right', color: colors.textMuted }}>{s.qtd || '—'}</td>
                      <td style={{ ...tdS, textAlign: 'right', color: s.qtd_nao_conciliados ? colors.warning : colors.textDim, fontSize: 12 }}>
                        {s.qtd_nao_conciliados ? `${s.qtd_nao_conciliados} · ${fmt(s.total_nao_conciliados)}` : '—'}
                      </td>
                      <td style={tdS}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 6,
                          background: s.ativo ? `${colors.success}22` : `${colors.textMuted}22`,
                          color: s.ativo ? colors.success : colors.textMuted }}>
                          {s.ativo ? 'Ativa' : 'Inativa'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.sem_categoria.rotulos.length > 0 && (
        <div style={{ marginTop: 16, fontSize: 12, color: colors.textMuted }}>
          <div style={{ fontWeight: 700, marginBottom: 6, color: colors.text }}>Rótulos ainda sem categoria do plano ({mes})</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {data.sem_categoria.rotulos.slice(0, 40).map((r, i) => (
              <span key={i} style={{ padding: '3px 8px', borderRadius: 6, background: colors.card, border: `1px solid ${colors.border}` }}
                title={`${r.grupo} · ${r.qtd} lançamento(s)`}>
                {r.grupo !== '(sem grupo)' ? `${r.grupo} / ` : ''}{r.categoria_rotulo} · <strong>{fmt(r.total)}</strong> ({r.qtd})
              </span>
            ))}
          </div>
        </div>
      )}

      {modal && (
        <LancamentosModal mes={mes} filtro={modal} opcoes={opcoes} podeEditar={podeEditar}
          onClose={() => setModal(null)} onChanged={carregar} />
      )}
    </div>
  );
}
